import { useId } from 'react';

import { Button } from '../../shared/ui/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader } from '../../shared/ui/Dialog';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import type { OpenAttemptRow } from './continue-api';
import { formatTimeLeft } from './open-attempts';
import s from './StartOverDialog.module.css';

export interface StartOverTarget {
  testId: string;
  testTitle: string;
  /** The unfinished attempt Continue returns to and Start over deletes. */
  attemptId: string;
  /** Its row from the open-attempts list, when that list was already loaded. */
  row: OpenAttemptRow | null;
}

export interface StartOverDialogProps {
  /** null ⇒ closed. */
  target: StartOverTarget | null;
  restarting: boolean;
  error: string | null;
  onContinue: () => void;
  onStartOver: () => void;
  onClose: () => void;
}

function progressLine(row: OpenAttemptRow): string {
  const parts: string[] = [];
  if (row.section_count > 1) {
    parts.push(`Section ${String(row.section_index)} of ${String(row.section_count)}`);
  }
  if (row.question_count > 0) {
    parts.push(`${String(row.answered_count)} of ${String(row.question_count)} answered`);
  }
  parts.push(formatTimeLeft(row.remaining_ms));
  return parts.join(' · ');
}

/**
 * "You've already started this test" — Continue it, or Start over.
 *
 * Continue is the primary action because it is the safe one and the usual
 * one. Start over is the destructive one, so the sentence above the buttons
 * names exactly what it deletes; the label stays short so the footer still
 * fits on a 320px phone.
 */
export function StartOverDialog({
  target,
  restarting,
  error,
  onContinue,
  onStartOver,
  onClose,
}: StartOverDialogProps): JSX.Element {
  const titleId = useId();
  const ledeId = useId();
  const consequenceId = useId();
  const row = target?.row ?? null;

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      labelledById={titleId}
      describedById={`${ledeId} ${consequenceId}`}
      size="sm"
      // ESC is handled by the native <dialog>, which closes itself before any
      // onClose guard can object — so mid-delete it must not be dismissible,
      // or the dialog shuts on an irreversible action whose outcome (or error)
      // then has nowhere to show.
      dismissible={!restarting}
    >
      <DialogHeader titleId={titleId} title="You’ve already started this test" onClose={onClose} />
      <DialogBody>
        <p id={ledeId} className={s.lede}>
          You have an unfinished attempt of{' '}
          <strong className={s.testTitle}>{target?.testTitle ?? ''}</strong>.
        </p>
        {row !== null ? <p className={s.progress}>{progressLine(row)}</p> : null}
        <p id={consequenceId} className={s.consequence}>
          Continue picks up where you stopped, with the time you had left. Start over deletes that
          attempt — your answers, highlights and notes, and any Writing or Speaking you saved — and
          can’t be undone.
        </p>
        {error !== null ? (
          <div className={s.error}>
            <StatusBanner tone="error">{error}</StatusBanner>
          </div>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <Button
          variant="danger"
          loading={restarting}
          loadingLabel="Starting over…"
          onClick={onStartOver}
        >
          Start over
        </Button>
        <Button disabled={restarting} onClick={onContinue}>
          Continue
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
